export type SupportedLocale = "en" | "ko" | "zh";
export const SUPPORTED_LOCALES: SupportedLocale[] = ["en", "ko", "zh"];
export const LOCALE_STORAGE_KEY = "locale";

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
	en: "English",
	ko: "한국어",
	zh: "中文",
};

export function isSupportedLocale(
	value: string | null,
): value is SupportedLocale {
	return value === "en" || value === "ko" || value === "zh";
}

export function detectLocale(): SupportedLocale {
	if (typeof navigator === "undefined") return "en";
	const lang = navigator.language.toLowerCase();
	if (lang.startsWith("ko")) return "ko";
	if (lang.startsWith("zh")) return "zh";
	return "en";
}

/**
 * Locale implied by a URL path prefix (`/ko/...`, `/ko`, `/zh/...`). Returns null
 * for the unprefixed English root. When present this is the AUTHORITATIVE locale,
 * so an SSR'd `/ko/...` page renders Korean regardless of stored prefs.
 */
export function localeFromPath(pathname: string): SupportedLocale | null {
	const match = pathname.match(/^\/(ko|zh)(?:\/|$)/);
	return match ? (match[1] as SupportedLocale) : null;
}

/** Strip a leading locale prefix → the logical (English) path (`/ko/learn/x` → `/learn/x`, `/ko` → `/`). */
export function stripLocalePrefix(pathname: string): string {
	const locale = localeFromPath(pathname);
	if (!locale) return pathname;
	const rest = pathname.slice(locale.length + 1);
	return rest === "" ? "/" : rest;
}

/** Re-apply a locale prefix to a logical path (`/` → `/ko`, `/learn/x` → `/ko/learn/x`). */
export function withLocalePrefix(
	path: string,
	locale: SupportedLocale,
): string {
	if (locale === "en") return path;
	return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

/**
 * Logical paths that have locale-prefixed mirrors (Phase 1: landing + learn).
 * Deliberately loose on the slug so router patterns like `/learn/$slug`
 * localize too; strict slug validation stays in seo.ts (`isLocalizablePath`)
 * where inputs are arbitrary URLs.
 */
export function isLocalizableLinkPath(path: string): boolean {
	return path === "/" || path === "/learn" || path.startsWith("/learn/");
}

/**
 * URL for `logicalPath` in `locale`, or null when that locale has no URL for
 * it (non-localizable page, or a locale without prefixed routes — zh).
 */
export function localizedPathFor(
	logicalPath: string,
	locale: SupportedLocale,
): string | null {
	if (!isLocalizableLinkPath(logicalPath)) return null;
	if (locale === "en") return logicalPath;
	// zh is UI-only for now (no /zh routes) — never emit a /zh/... href.
	if (locale !== "ko") return null;
	return withLocalePrefix(logicalPath, locale);
}

export function readLocale(pathname?: string): SupportedLocale {
	if (typeof window === "undefined") return "en";
	// URL prefix wins (authoritative for SSR), then stored, then browser.
	// pathname 인자: SPA 내비게이션 중에는 라우터 상태가 window.location(history
	// 커밋)보다 먼저 갱신되는 구간이 있어, 라우터가 아는 경로를 넘겨 이전 URL의
	// locale로 판정하는 레이스를 피한다 (LocaleSync가 사용).
	const fromPath = localeFromPath(pathname ?? window.location.pathname);
	if (fromPath) return fromPath;
	const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
	if (isSupportedLocale(stored)) return stored;
	return detectLocale();
}

export function writeLocale(locale: SupportedLocale): void {
	if (typeof window === "undefined") return;
	window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

export function applyLocale(locale: SupportedLocale): void {
	if (typeof document === "undefined") return;
	document.documentElement.lang = locale;
}

/**
 * 클라이언트가 SSR 로케일(URL 기준)과 다른 선호 로케일로 전환했는지 — LocaleSync가 기록,
 * client.tsx의 onRecoverableError가 읽는다. 전환은 마운트 후 idle 타임에 하지만 React는
 * Suspense 경계(코드 스플릿 라우트·lazy 청크)를 별도 태스크로 늦게 hydrate하므로, 아직
 * hydrate 안 된 경계가 전환된 언어로 그려지면 텍스트 mismatch → React가 그 서브트리를
 * 클라이언트 렌더로 복구한다(SPA 시절과 같은 결과). 그 복구는 예상된 것이라 Sentry에서 뺀다.
 * ponytail: 근본 해법은 로케일을 URL/쿠키로 서버에 알리는 것 — 정적 프리렌더와 충돌해 보류.
 */
let localeSwitchedAfterHydration = false;
export function markLocaleSwitchedAfterHydration(): void {
	localeSwitchedAfterHydration = true;
}
export function didLocaleSwitchAfterHydration(): boolean {
	return localeSwitchedAfterHydration;
}
